import { useState } from "react";
import { useDeviceHistory } from "hooks/useDevices";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import {
  TableCard,
  TableToolbar,
  ToolbarCount,
  ToolbarFilters,
  FilteredBadge,
} from "styles/components/deviceAlarms.styles";
import { HistoryTableWrapper } from "styles/devices/deviceHistory.styles";
import { useAuth } from "context/AuthContext";
import { DeviceHistoryItem } from "types/deviceHooks.types";
import { LoadingScreen } from "./shared/LoadingScreen";
import { PageError } from "./shared/PageError";
import { filteredEmpty, zeroData } from "utils/emptyCopy";
import {
  actionTemplate,
  dateTemplate,
  eventTypeTemplate,
  formatEventType,
  matchesHistoryFilters,
  sourceTemplate,
} from "./history/historyTableHelpers";

interface DeviceHistoryProps {
  serialNumber: string;
}

export function DeviceHistory({ serialNumber }: DeviceHistoryProps) {
  const { data, isLoading, error, refetch } = useDeviceHistory(serialNumber);
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [filterEventType, setFilterEventType] = useState<string | null>(null);
  const [filterSource, setFilterSource] = useState<string | null>(null);
  const [filterText, setFilterText] = useState("");

  if (isLoading) {
    return <LoadingScreen message="Loading device history..." />;
  }

  if (error || !data || !data.success) {
    return (
      <PageError
        title="Error loading device history"
        message={
          error instanceof Error ? error.message : "Please try again later."
        }
        onRetry={() => refetch()}
      />
    );
  }

  const filteredHistory = isAdmin
    ? data.response
    : data.response.filter(
        (item: DeviceHistoryItem) => item.eventType !== "CONFIG_UPDATED",
      );

  const eventTypeOptions = [
    { label: "All Actions", value: "" },
    ...Array.from(
      new Set(filteredHistory.map((i: DeviceHistoryItem) => i.eventType)),
    ).map((type) => ({ label: formatEventType(type as string), value: type })),
  ];

  const sourceOptions = [
    { label: "All Origins", value: "" },
    { label: "App", value: "APP" },
    { label: "Portal", value: "PORTAL" },
    { label: "Device", value: "DEVICE" },
  ];

  const displayedHistory = filteredHistory.filter((item: DeviceHistoryItem) =>
    matchesHistoryFilters(item, filterEventType, filterSource, filterText),
  );

  const hasActiveFilters = !!filterEventType || !!filterSource || !!filterText;

  return (
    <HistoryTableWrapper>
      <TableCard>
        <TableToolbar>
          <ToolbarCount>
            <i className="pi pi-history" />
            {displayedHistory.length} record
            {displayedHistory.length !== 1 ? "s" : ""}
            {hasActiveFilters && (
              <FilteredBadge>of {filteredHistory.length} total</FilteredBadge>
            )}
          </ToolbarCount>

          <ToolbarFilters>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                placeholder="Search..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </span>
            <Dropdown
              options={eventTypeOptions}
              optionLabel="label"
              optionValue="value"
              value={filterEventType ?? ""}
              onChange={(e) =>
                setFilterEventType(e.value === "" ? null : e.value)
              }
              placeholder="All Actions"
              style={{ minWidth: "160px" }}
            />
            <Dropdown
              options={sourceOptions}
              optionLabel="label"
              optionValue="value"
              value={filterSource ?? ""}
              onChange={(e) => setFilterSource(e.value === "" ? null : e.value)}
              placeholder="All Origins"
              style={{ minWidth: "140px" }}
            />
            {hasActiveFilters && (
              <Button
                icon="pi pi-times"
                label="Clear"
                className="p-button-text p-button-sm"
                onClick={() => {
                  setFilterEventType(null);
                  setFilterSource(null);
                  setFilterText("");
                }}
              />
            )}
          </ToolbarFilters>
        </TableToolbar>

        <DataTable
          value={displayedHistory}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          tableStyle={{ minWidth: "40rem" }}
          stripedRows
          emptyMessage={
            hasActiveFilters
              ? filteredEmpty("history records")
              : zeroData("history records")
          }
        >
          <Column
            field="eventType"
            header="Action"
            body={eventTypeTemplate}
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="event.request"
            header="Details"
            body={actionTemplate}
            style={{ width: "45%" }}
          />
          <Column
            field="changedFrom"
            header="Origin"
            body={sourceTemplate}
            sortable
            style={{ width: "12%" }}
          />
          <Column
            field="createdAt"
            header="Date"
            body={dateTemplate}
            sortable
            style={{ width: "23%" }}
          />
        </DataTable>
      </TableCard>
    </HistoryTableWrapper>
  );
}
